import { IfAbility } from '@efdigital/ll-addon-auth';
import formatDate, {
  DateFormat,
} from '@efdigital/ll-addon-content/helpers/format-date';
import type { Client } from '@efdigital/ll-addon-data';
import { DataTypeSystem } from '@efdigital/ll-addon-data/data-types';
import { Heading } from '@efdigital/ll-addon-ui';
import { EditClient } from '@efdigital/ll-admin-v2/components/client-edit/edit-client.gts';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { PaginationByOffset } from '@efdigital/ll-admin-v2/components/pagination.gts';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import {
  SearchClients,
  type SearchClientsState,
} from '@efdigital/ll-admin-v2/components/search/search-clients.gts';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback.ts';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { Value } from '@warp-drive/core/types/json/raw';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

function getTotal(page: Value | undefined): number {
  if (typeof page === 'object' && page !== null && 'total' in page) {
    return page.total as number;
  }
  return 0;
}

export default class LibraryClientsIndex extends Component {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked modalOpen = false;
  @tracked newClient: Client | null = null;

  @tracked state: SearchClientsState = {
    offset: 0,
    limit: 30,
    title: '',
    subscription: '',
    view: '',
    domain: '',
    connection: '',
    username: '',
    expires: 'future',
    sortField: 'title',
  };

  setState = (partial: Partial<SearchClientsState>) => {
    this.state = { ...this.state, ...partial };
  };

  setOffset = (offset: number): void => {
    this.setState({ offset });
  };

  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleDateString();
  }

  isExpired(expiresDate: string | undefined): boolean {
    if (!expiresDate) {
      return false;
    }
    return new Date(expiresDate) < new Date();
  }

  expiresSoon(expiresDate: string | undefined): boolean {
    if (!expiresDate) {
      return false;
    }
    const expires = new Date(expiresDate);
    const now = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(now.getDate() + 7);
    return expires >= now && expires <= sevenDaysFromNow;
  }

  showCreateModal = () => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    this.newClient = this.store.createRecord(DataTypeSystem.Client, {
      subscription: 'full',
      view: 'va-program',
      expires: oneYearFromNow.toISOString(),
    });
    this.modalOpen = true;
  };

  hideCreateModal = () => {
    if (this.newClient) {
      deepRollback(this.newClient, this.store);
    }
    this.modalOpen = false;
    this.newClient = null;
  };

  clientCreated = () => {
    if (this.newClient?.id) {
      this.router.transitionTo(
        'library.clients.client.edit.general',
        this.newClient.id
      );
    }
  };

  <template>
    <div class="container py-5">
      <Heading
        @text="Client Library"
        @secondary="Browse and manage clients. Filter by title, subscription type, view, domains, and more."
      />

      <IfAbility @ability="clients" @atLeast="viewer">
        <:if>
          <IfAbility @ability="clients" @atLeast="editor">
            <:if>
              <div class="d-flex justify-content-center mb-5">
                <button
                  type="button"
                  class="btn btn-primary btn-md"
                  {{on "click" this.showCreateModal}}
                >New Client</button>
              </div>
            </:if>
          </IfAbility>

          <SearchClients @state={{this.state}} @setState={{this.setState}}>
            <:query as |query|>
              {{! Results }}
              <Request @query={{query}}>
                <:content as |response|>
                  {{#if response.data.length}}
                    <div class="d-flex flex-row justify-content-between my-4">
                      <p class="text-muted">
                        Showing
                        {{response.data.length}}
                        of
                        {{getTotal response.meta.page}}
                        clients
                      </p>

                      <PaginationByOffset
                        @offset={{this.state.offset}}
                        @limit={{this.state.limit}}
                        @total={{getTotal response.meta.page}}
                        @onOffsetChange={{this.setOffset}}
                      />
                    </div>

                    <ResultsTable @data={{response.data}}>
                      <:header>
                        <th scope="col">Title</th>
                        <th scope="col">Expires</th>
                        <th scope="col" class="col-md-5">Users</th>
                        <th scope="col">Status</th>
                      </:header>
                      <:row as |client|>
                        {{#let (this.isExpired client.expires) as |isExpired|}}
                          {{#let
                            (this.expiresSoon client.expires)
                            as |expiresSoon|
                          }}
                            <tr>
                              <th scope="row">
                                <LinkTo
                                  @route="library.clients.client.edit.general"
                                  @model={{client.id}}
                                  class="fw-bold
                                    {{if
                                      isExpired
                                      'text-danger text-decoration-line-through'
                                      (if
                                        expiresSoon
                                        'text-warning text-decoration-line-through'
                                        'text-decoration-none'
                                      )
                                    }}"
                                >
                                  {{client.title}}
                                </LinkTo>
                              </th>
                              <td class="font-monospace">
                                {{formatDate
                                  client.expires
                                  DateFormat.ShortDate
                                }}
                              </td>
                              <td class="col-md-5">
                                {{#each client.connections as |connection|}}
                                  <span
                                    class="badge bg-black me-1"
                                  ><iconify-icon inline icon="ph:users-bold" />
                                    {{connection}}</span>
                                {{/each}}
                                {{#each client.domains as |domain|}}
                                  <span
                                    class="badge bg-secondary me-1"
                                  ><iconify-icon inline icon="ph:at-bold" />
                                    {{domain}}</span>
                                {{/each}}
                              </td>
                              <td>
                                {{#if isExpired}}
                                  <span class="badge bg-danger">Expired</span>
                                {{else if expiresSoon}}
                                  <span class="badge bg-warning">Expires Soon</span>
                                {{/if}}
                              </td>
                            </tr>
                          {{/let}}
                        {{/let}}
                      </:row>
                      <:empty>
                        <div class="alert alert-info" role="alert">No clients
                          found matching your filters.</div>
                      </:empty>
                    </ResultsTable>

                    <PaginationByOffset
                      @offset={{this.state.offset}}
                      @limit={{this.state.limit}}
                      @total={{getTotal response.meta.page}}
                      @onOffsetChange={{this.setOffset}}
                      class="mt-4 d-flex justify-content-center"
                    />
                  {{else}}
                    <div class="alert alert-info">
                      No clients found matching your filters.
                    </div>
                  {{/if}}
                </:content>

                <:loading>
                  <div class="text-center py-5">
                    <div class="spinner-border" role="status">
                      <span class="visually-hidden">Loading clients...</span>
                    </div>
                  </div>
                </:loading>

                <:error as |error|>
                  <div class="alert alert-danger">
                    Error loading clients:
                    {{error.message}}
                  </div>
                </:error>
              </Request>
            </:query>
          </SearchClients>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view clients.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>

      {{! New Client Modal }}
      {{#if this.modalOpen}}
        <BsModal
          @open={{this.modalOpen}}
          @onHidden={{this.hideCreateModal}}
          @backdropClose={{false}}
          @size="lg"
          as |modal|
        >
          <modal.header>
            <h3 class="modal-title h4">Create Client</h3>
          </modal.header>
          <modal.body>
            {{#if this.newClient}}
              <EditContext
                @onEditingStopped={{this.clientCreated}}
                as |context|
              >
                <EditClient @item={{this.newClient}} @context={{context}} />
              </EditContext>
            {{/if}}
          </modal.body>
        </BsModal>
      {{/if}}
    </div>
  </template>
}
